import { Injectable } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { getFullPath } from '../lib/get-full-path';
import { DialogService } from './dialog.service';
import { StateService } from './state.service';
import { UserService } from './user.service';

@Injectable({
  providedIn: 'root',
})
export class NewFeatureDotService {
  private _updates: Subject<[string, boolean]> = new BehaviorSubject<[string, boolean]>(undefined);
  public readonly onUpdates: Observable<[string, boolean]> = this._updates.asObservable();

  featureInfo: {
    key: string;
    added: number;
    for: 'existing' | 'new';
    path?: string;
    dialog?: string;
    showAfterOnboarding?: boolean;
  }[] = [
    {
      key: 'advanced-mode',
      added: new Date('2022/10/25').getTime(),
      for: 'new',
      path: '/advanced',
    },
    {
      key: 'gallery-settings-review',
      added: new Date('2022/10/25').getTime(),
      for: 'new',
    },
    {
      key: 'branding',
      added: new Date('2022/10/25').getTime(),
      for: 'new',
      path: '/settings/branding',
      showAfterOnboarding: true,
    },
  ];

  constructor(
    private userService: UserService,
    private state: StateService,
    private router: Router,
    private route: ActivatedRoute,
    private dialog: DialogService
  ) {
    window['resetSeenFeatures'] = async () => {
      const seenFeatures = this.getSeenFeatures();
      this.state.user.questionnaire.seenFeatures = JSON.stringify([]);
      seenFeatures.forEach(f => this._updates.next([f, true]));
      await this.userService.updateUser({ questionnaire: this.state.user.questionnaire });
    };
    this.router.events.subscribe(event => {
      if (!(event instanceof NavigationEnd)) return;
      this.checkPath();
    });
    this.dialog.onDialogOpen.subscribe(event => {
      if (!event) return;
      const dialogName = new event.component().dialogName;
      this.featureInfo
        .filter(feature => feature.dialog && feature.dialog === dialogName)
        .forEach(feature => this.markFeatureAsSeen(feature.key));
    });
    this.checkPath();
  }

  private checkPath() {
    const path = getFullPath(this.route.snapshot).join('/');
    this.featureInfo
      .filter(feature => feature.path && path.endsWith(feature.path))
      .forEach(feature => this.markFeatureAsSeen(feature.key));
  }

  public async markFeatureAsSeen(feature: string) {
    const seenFeatures = this.getSeenFeatures();
    if (seenFeatures.includes(feature)) return;
    seenFeatures.push(feature);
    this.state.user.questionnaire.seenFeatures = JSON.stringify(seenFeatures);
    this._updates.next([feature, false]);
    return this.userService.updateUser({ questionnaire: this.state.user.questionnaire });
  }

  public showFeatureAsNew(feature: string) {
    const info = this.featureInfo.find(f => f.key === feature);

    if (info) {
      if (info.for === 'new' && info.added > this.state.user.created_at * 1000) return false;
      if (info.for === 'existing' && info.added < this.state.user.created_at * 1000) return false;
      if (info.showAfterOnboarding && !this.state.user.questionnaire.done) return false;
    }
    return !this.getSeenFeatures().includes(feature);
  }

  private getSeenFeatures(): string[] {
    if (!this.state.user.questionnaire.seenFeatures) return [];
    return JSON.parse(<string>this.state.user.questionnaire.seenFeatures);
  }
}
